import React, { useCallback, useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  BrukerrollerDto,
  BrukerrollerPSDto,
  PraksisstedbrukerDto,
  PraksisstedInfoPutDto,
  PraksisstedprofilDto,
} from '../../api/generated/studiested-generated-api';
import translations from './practiceplaceprofile.i18n';
import Button from '../../components/button';
import PageLayout from '../../pagelayout';
import { Grid } from '@mui/material';
import * as css from './practiceplaceprofile.module.scss';
import classnames from 'classnames';
import PanelHeader from '../../components/panelheader/panelHeader';
import { formatUrlNoScheme } from '../utils';
import { UhaUserContext } from '../../user/UhaUserStore';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import { Link } from 'react-router-dom';
import { EmptyState } from '../../components/emptyState/emptyState';
import { reduceToEditProfilType } from './practiceplaceprofileutils';
import {
  ActionItems,
  Bilde,
  EditProfileModal,
  LastEditedBy,
  hasLastEditedByInfo,
} from './commoncomponents';

enum BrukerColumns {
  NAVN,
  STATUS,
  ROLLE,
  EPOST,
  OPPRETTET_AV,
}

const PracticePlaceUsers: React.FC<
  React.PropsWithChildren<{
    brukere: PraksisstedbrukerDto[];
  }>
> = ({ brukere }) => {
  const { uhaUser } = useContext(UhaUserContext);
  const isAdmin = uhaUser?.hoeyesteRettighet === BrukerrollerDto.Administrator;

  const { formatMessage, locale } = useIntl();
  const translateRolle = useCallback(
    (rolle: BrukerrollerPSDto) =>
      formatMessage({ id: translations[`rolle${rolle}`].id }),
    [formatMessage],
  );
  const translateStatus = (deaktivert: boolean, placeholder: boolean) => {
    if (placeholder) {
      return formatMessage({
        id: translations.statusPlaceholder.id,
      });
    }

    return formatMessage({
      id: translations[`${deaktivert ? 'statusDeaktivert' : 'statusAktiv'}`].id,
    });
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = (
    col: number,
    a: PraksisstedbrukerDto,
    b: PraksisstedbrukerDto,
  ) => {
    switch (col) {
      case 0:
        return compare(a.etternavn, b.etternavn);
      case 1:
        return compare(
          translateStatus(!!a.deaktivert, !!a.plassholder),
          translateStatus(!!b.deaktivert, !!b.plassholder),
        );
      case 2:
        return compare(
          translateRolle(a.tilgangsrolle),
          translateRolle(b.tilgangsrolle),
        );
      case 3:
        return compare(a.epost, b.epost);
      case 4:
        compare(a.lagtTilAvInstitusjon || '', b.lagtTilAvInstitusjon || '');
    }
    return 0;
  };

  const getSortedUsersByActiveCol = (col: number, ascending: boolean) =>
    [...brukere].sort((a, b) => {
      return ascending ? sort(col, b, a) : sort(col, a, b);
    });

  const [activeCol, setActiveCol] = useState<number>(0);
  const [sortedUsers, setSortedUsers] = useState<PraksisstedbrukerDto[]>(
    getSortedUsersByActiveCol(0, false),
  );
  const [search, setSearch] = useState<string[]>(['', '', '', '']);

  const labels = {
    brukerrettighet: formatMessage({ id: translations.brukerrettighet.id }),
    status: formatMessage({ id: translations.status.id }),
    epost: formatMessage({ id: translations.epost.id }),
    navn: formatMessage({ id: translations.navn.id }),
    opprettetAv: formatMessage({ id: translations.opprettetAv.id }),
    praksisstedbrukere: formatMessage({
      id: translations.praksisstedbrukere.id,
    }),
    praksisstedbrukereSubtitle: formatMessage({
      id: translations.praksisstedbrukereSubtitle.id,
    }),
    emptyPraksisstedbrukere: formatMessage({
      id: translations.emptyPraksisstedbrukere.id,
    }),
    emptyPraksisstedbrukereSubtitle: formatMessage({
      id: translations.emptyPraksisstedbrukereSubtitle.id,
    }),
  };

  const headers = [
    {
      title: labels.navn,
      type: BrukerColumns.NAVN,
    },
    {
      title: labels.status,
      type: BrukerColumns.STATUS,
    },
    {
      title: labels.brukerrettighet,
      type: BrukerColumns.ROLLE,
    },
    {
      title: labels.epost,
      type: BrukerColumns.EPOST,
    },
    {
      title: labels.opprettetAv,
      type: BrukerColumns.OPPRETTET_AV,
    },
  ];

  const filteredAndSortedUsers = React.useMemo(() => {
    const navnSearch = (
      fornavn: string,
      etternavn: string,
      searchIndex: number,
    ) => {
      return (
        `${fornavn} ${etternavn}`.toLowerCase().indexOf(search[searchIndex]) >
        -1
      );
    };

    return sortedUsers.filter((user) => {
      const filterNavn =
        search[0].length === 0 || navnSearch(user.fornavn, user.etternavn, 0);
      const filterRolle =
        search[1].length === 0 ||
        (user.telefonnummer &&
          translateRolle(user.tilgangsrolle).toLowerCase().indexOf(search[1]) >
            -1);
      const filterEpost =
        search[2].length === 0 ||
        (user.epost && user.epost.toLowerCase().indexOf(search[2]) > -1);
      const filterOpprettetAv =
        search[3].length === 0 ||
        (user.lagtTilAvInstitusjon &&
          user.lagtTilAvInstitusjon.toLowerCase().indexOf(search[3]) > -1);

      return filterNavn && filterRolle && filterEpost && filterOpprettetAv;
    });
  }, [sortedUsers, search, translateRolle]);

  const getRows = () =>
    filteredAndSortedUsers.map((bruker) => {
      let tabHeader = 0;

      const navn = `${bruker.fornavn} ${bruker.etternavn}`;

      return (
        <TableRow key={bruker.id}>
          <TableCellTd header={headers[tabHeader++].title}>
            {bruker.plassholder || bruker.deaktivert ? (
              <>{navn}</>
            ) : (
              <Link to={`/practiceplaces/praksisstedbrukerprofil/${bruker.id}`}>
                {navn}
              </Link>
            )}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {translateStatus(!!bruker.deaktivert, !!bruker.plassholder)}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {translateRolle(bruker.tilgangsrolle)}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {bruker.epost}
          </TableCellTd>
          <TableCellTd header={headers[tabHeader++].title}>
            {bruker.lagtTilAvInstitusjon}
          </TableCellTd>
        </TableRow>
      );
    });

  const sortTilganger = (col: number, ascending: boolean) =>
    setSortedUsers(getSortedUsersByActiveCol(col, ascending));

  const canSortIndices = [0, 1, 2, 3, 4];
  const canSearchIndices = [0, 2, 3, 4];

  const getHeaders = () =>
    headers.map(({ title, type }) => (
      <TableCellTh
        key={title}
        colIndex={type}
        activeCol={type === activeCol}
        setActiveCol={setActiveCol}
        sort={sortTilganger}
        canSort={canSortIndices.includes(type)}
        canSearch={canSearchIndices.includes(type)}
        search={(col, value) =>
          setSearch(
            search.map((searchString, i) =>
              canSearchIndices.indexOf(col) === i
                ? value.toLowerCase()
                : searchString,
            ),
          )
        }
      >
        {title}
      </TableCellTh>
    ));

  return isAdmin ? (
    <>
      <PanelHeader
        className={css.practicePlaceHeader}
        title={labels.praksisstedbrukere}
        subtitle={labels.praksisstedbrukereSubtitle}
      />
      {brukere.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow top>{getHeaders()}</TableRow>
          </TableHeader>
          <TableMain>{getRows()}</TableMain>
        </Table>
      ) : (
        <EmptyState
          title={labels.emptyPraksisstedbrukere}
          text={labels.emptyPraksisstedbrukereSubtitle}
        />
      )}
    </>
  ) : (
    <></>
  );
};

interface PracticePlaceProfileProps {
  data: PraksisstedprofilDto;
  menuItems: React.ReactNode;
  canEdit: boolean;
  isAdmin: boolean;
  saveHandler: (beskrivelse: string, krav: string) => void;
  saveContactInfo: (updatedPraksissted: PraksisstedInfoPutDto) => void;
}

const PracticePlaceProfileUha: React.FC<
  React.PropsWithChildren<PracticePlaceProfileProps>
> = ({ data, menuItems, canEdit, isAdmin, saveHandler, saveContactInfo }) => {
  const { formatMessage } = useIntl();

  const cancelEdit = () => {
    setIsEditMode(false);
  };

  const saveEdit = () => {
    saveHandler(beskrivelse, krav);
    setIsEditMode(false);
  };

  const labels = {
    title: formatMessage(
      { id: translations.title.id },
      { practicePlaceName: data.navn },
    ),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    profileImgName: formatMessage(
      { id: translations.profileImgName.id },
      { practiceplaceName: data.navn },
    ),
    eier: formatMessage({ id: translations.eier.id }),
    orgnr: formatMessage({ id: translations.orgnr.id }),
    kontaktinfo: formatMessage({ id: translations.kontaktinfo.id }),
    epost: formatMessage({ id: translations.epost.id }),
    telefon: formatMessage({ id: translations.telefon.id }),
    nettside: formatMessage({ id: translations.nettside.id }),
    beskrivelse: formatMessage({ id: translations.beskrivelse.id }),
    krav: formatMessage({ id: translations.krav.id }),
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [beskrivelse, setBeskrivelse] = useState(data.beskrivelse || '');
  const [krav, setKrav] = useState(data.krav || '');
  const [isOpen, setIsOpen] = useState(false);

  const actionItems = (
    <ActionItems
      backPath={'/practiceplaces'}
      isEditMode={isEditMode}
      setIsEditMode={setIsEditMode}
      canEdit={canEdit}
      cancelEdit={cancelEdit}
      saveEdit={saveEdit}
    />
  );

  return (
    <PageLayout
      isIndented={true}
      title={labels.title}
      subtitle={labels.subtitle}
      menuItems={menuItems}
      actionItems={actionItems}
    >
      <EditProfileModal
        data={reduceToEditProfilType(data)}
        isOpen={isOpen}
        handleSaveContactInfo={saveContactInfo}
        handleClose={() => setIsOpen(false)}
      />
      <Grid container className={css.container} alignItems={'stretch'}>
        <Grid item xs={12} sm={12} md={8} lg={7} xl={6}>
          <Bilde
            bildeFileId={data.bildeId}
            bildeFilename={labels.profileImgName}
            praksisstedId={data.id}
            isEditMode={isEditMode}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={5} xl={6} className={css.info}>
          <div
            className={classnames(
              css.label,
              css.practicePlaceName,
              'practiceplacename',
            )}
          >
            {data.navn}
          </div>
          {data.orgnr && (
            <div className={css.orgnr}>
              <span className={css.label}>{labels.orgnr}:</span>{' '}
              <span className="organizationNumber">{data.orgnr}</span>
            </div>
          )}
          {data.avdeling && (
            <div>
              <span className={css.label}>{labels.eier}:</span>{' '}
              <Link to={`/practiceplaces/profile/${data.avdeling.id}`}>
                <span className="departmentName">{data.avdeling.navn}</span>
              </Link>
            </div>
          )}
          <PanelHeader title={labels.kontaktinfo} backgroundColor={'#ffffff'} />
          {data.adresse && (
            <>
              {data.adresse.adresselinje1 && (
                <div className="streetAddress">
                  {data.adresse.adresselinje1}
                </div>
              )}
              {data.adresse.adresselinje2 && (
                <div className="streetAddress">
                  {data.adresse.adresselinje2}
                </div>
              )}
              <div className="postalAddress">
                {data.adresse.postnr} {data.adresse.poststed}
              </div>
            </>
          )}
          {data.nettsideUrl && (
            <div>
              <span className={css.label}>{labels.nettside}:</span>{' '}
              <a href={data.nettsideUrl} className="websiteUrl">
                {formatUrlNoScheme(data.nettsideUrl)}
              </a>
            </div>
          )}
          {data.epost && (
            <div>
              <span className={css.label}>{labels.epost}:</span>{' '}
              <a href={`mailto: ${data.epost}`} className="email">
                {data.epost}
              </a>
            </div>
          )}
          {data.telefonlandnr && data.telefonnummer && (
            <div>
              <span className={css.label}>{labels.telefon}:</span>{' '}
              <span className="phone">{`${data.telefonlandnr} ${data.telefonnummer}`}</span>
            </div>
          )}
          {isEditMode && isAdmin && (
            <div className={css.editProfileButtonContainer}>
              <Button.Secondary onClick={() => setIsOpen(true)}>
                {formatMessage({ id: translations.endreProfil.id })}
              </Button.Secondary>
            </div>
          )}
        </Grid>
      </Grid>
      <Grid container={true} className={css.container}>
        {(data.beskrivelse || isEditMode) && (
          <Grid item={true} xs={12} sm={7}>
            <PanelHeader title={labels.beskrivelse} />
            {isEditMode ? (
              <textarea
                value={beskrivelse}
                onChange={(e) => setBeskrivelse(e.target.value)}
              />
            ) : (
              <p>{data.beskrivelse}</p>
            )}
          </Grid>
        )}
        {(data.krav || isEditMode) && (
          <Grid item={true} xs={12} sm={7}>
            <PanelHeader title={labels.krav} />
            {isEditMode ? (
              <textarea
                value={krav}
                onChange={(e) => setKrav(e.target.value)}
              />
            ) : (
              <p>{data.krav}</p>
            )}
          </Grid>
        )}
        {hasLastEditedByInfo(data) && (
          <>
            <Grid item={true} xs={7}>
              <LastEditedBy
                className={css.lastEditedBy}
                praksisstedprofil={data}
              />
            </Grid>
            <Grid item={true} xs={5} />
          </>
        )}
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <PracticePlaceUsers brukere={data.brukere || []} />
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export default PracticePlaceProfileUha;
